import React, { useState, useEffect } from "react";
import styled from "styled-components";
import NewGrammarGame from "../../components/NewGrammarGame"; // 🌟 Directory path updated here
import { Scale } from 'react-scaling';
import Papa from "papaparse";

let filteredQuestions = [];

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

function ProcessRowToQuestion(row) {
  if (!row || row.length < 8) return null;

  let questionText = row[6] ? row[6].toString() : "";
  let questionLine2 = row[7] ? row[7].toString() : "";

  let rawIncorrect = [row[8], row[9], row[10], row[11], row[12], row[13]];
  let rawCorrect = [row[14], row[15], row[16], row[17]];

  let questionAnswers = [];
  let cleanCorrect = [];

  rawCorrect.forEach(item => {
    if (item !== undefined && item !== null) {
      let trimmed = item.toString().trim();
      if (trimmed !== "" && !cleanCorrect.includes(trimmed)) {
        cleanCorrect.push(trimmed);
      }
    }
  });

  let renderedChoices = [];

  cleanCorrect.forEach(val => {
    if (!renderedChoices.includes(val)) {
      renderedChoices.push(val);
      questionAnswers.push({ correct: true, value: val, selected: false });
    }
  });

  rawIncorrect.forEach(item => {
    if (item !== undefined && item !== null) {
      let trimmed = item.toString().trim();
      if (trimmed !== "" && !cleanCorrect.includes(trimmed) && !renderedChoices.includes(trimmed)) {
        renderedChoices.push(trimmed);
        questionAnswers.push({ correct: false, value: trimmed, selected: false });
      }
    }
  });

  shuffleArray(questionAnswers);
  
  // Joined with a comma for readable feedback presentation
  let correctPayloadString = cleanCorrect.join(", ");

  return {
    question: questionText,
    question_line_2: questionLine2,
    answers: questionAnswers,
    topic_id: row[4] || "grammar_assessment",
    correct: correctPayloadString,
    question_no: parseInt(row[5]) || 0
  };
}

export default function GrammarAssessment({ ...props }) {
  const [loading, setLoading] = useState(true);
  const [correctQuestions, setCorrectQuestions] = useState([]);
  const [incorrectQuestions, setIncorrectQuestions] = useState([]);
  const [score, setScore] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(undefined);
  const [showColouredFeedback, setColouredFeedback] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchCSV = async () => {
      try {
        const response = await fetch("/questions/grammar_assessments.csv");
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (results) => {
            const id = props.assessment_id || "";
            const parts = id.split('_');
            const sanitize = (str) => (str || "").toString().replace(/\s/g, "").toLowerCase();

            const targetYear = parts.length > 1 ? sanitize(parts[1]) : "";   
            const targetType = parts.length > 2 ? sanitize(parts[2]) : "";   
            const targetPaper = parts.length > 3 ? sanitize(parts[3]) : "";  

            const matches = results.data.slice(1).filter((row) => {
              if (row.length < 4) return false;
              
              const rowYear = sanitize(row[0]);  
              const rowType = sanitize(row[1]);  
              const rowPaper = sanitize(row[2]); 

              return (targetYear === "" || rowYear === targetYear) &&
                     (targetType === "" || rowType === targetType) &&
                     (targetPaper === "" || rowPaper === targetPaper);
            })
            .map(row => ProcessRowToQuestion(row))
            .filter(q => q !== null)
            .sort((a, b) => a.question_no - b.question_no);

            filteredQuestions = matches;
            if (filteredQuestions.length > 0) {
              setCurrentQuestion(filteredQuestions[0]);
            } else {
              setCurrentQuestion(null); 
            }
            setLoading(false);
          }
        });
      } catch (e) { 
        console.error("Critical error reading Grammar Assessment CSV:", e);
        setLoading(false); 
      }
    };
    fetchCSV();
  }, [props.assessment_id]);

  const handleToggleOption = (answerIndex) => {
    if (showColouredFeedback || !currentQuestion) return;
    currentQuestion.answers[answerIndex].selected = !currentQuestion.answers[answerIndex].selected;
    setRefreshTrigger(!refreshTrigger); 
  };

  const advanceToNextQuestion = () => {
    setColouredFeedback(false);
    const nextIdx = currentIndex + 1;
    
    if (nextIdx < filteredQuestions.length) {
      setCurrentIndex(nextIdx);
      setCurrentQuestion(filteredQuestions[nextIdx]);
    } else {
      setCurrentIndex(0);
      setCurrentQuestion(null); 
    }
    setIsProcessing(false);
  };

  const handleSubmitAnswer = async () => {
    if (isProcessing || !currentQuestion || showColouredFeedback) return;
    setIsProcessing(true);

    let isCorrect = true;
    let selectedChoices = [];

    for (let i = 0; i < currentQuestion.answers.length; i++) {
      let optionItem = currentQuestion.answers[i];
      if (optionItem.correct !== optionItem.selected) {
        isCorrect = false;
      }
      if (optionItem.selected) {
        selectedChoices.push(optionItem.value);
      }
    }

    let joinedUserAnswerPayload = selectedChoices.join(", ");

    // Keys aligned to match AssessmentResult spelling component targets
    const submissionData = {
      question: currentQuestion.question + (currentQuestion.question_line_2 ? "<br />" + currentQuestion.question_line_2 : ""),
      word: currentQuestion.correct,        // Target value for the green card container box
      answer: joinedUserAnswerPayload,     // Target value for the red card container box
      is_correct_boolean: isCorrect
    };

    if (isCorrect) {
      setCorrectQuestions(prev => [...prev, submissionData]);
      setScore(prev => prev + 10);
      advanceToNextQuestion();
    } else {
      setIncorrectQuestions(prev => [...prev, submissionData]);
      if (score >= 10) {
        setScore(prev => prev - 10);
      }
      setColouredFeedback(true);
      setTimeout(() => {
        advanceToNextQuestion();
      }, 2000);
    }
  };

  return (
    <NewGrammarGame
      onBack={props.onBack}
      correct={correctQuestions}
      incorrect={incorrectQuestions}
      score={score}
      defaultTimer={props.timelimit > 0 ? props.timelimit : 1200}
      questionLimit={filteredQuestions.length}
      loading={loading}
      feedback={true}
      parent={props}
    >
      <Body>
        <Scale scaleTo={2200}>
          {currentQuestion && (
            <Container>
              <Question>
                <div dangerouslySetInnerHTML={{ __html: currentQuestion.question.replace("$", "_") }} />
              </Question>
              
              {currentQuestion.question_line_2 !== "" && (
                <Question>
                  <div dangerouslySetInnerHTML={{ __html: currentQuestion.question_line_2 }} />
                </Question>
              )}

              <div>
                {currentQuestion.answers && currentQuestion.answers.map((answer, idx) => {
                  if (!showColouredFeedback) {
                    return (
                      <div style={{ width: "100%", margin: "0px" }} key={idx}>
                        {answer.selected ? (
                          <SelectedButton onClick={() => handleToggleOption(idx)}>
                            <div dangerouslySetInnerHTML={{ __html: answer.value }} />
                          </SelectedButton>
                        ) : (
                          <Button onClick={() => handleToggleOption(idx)}>
                            <div dangerouslySetInnerHTML={{ __html: answer.value }} />
                          </Button>
                        )}
                      </div>
                    );
                  }

                  const feedbackStyles = answer.correct 
                    ? { border: "2px solid #19a03f", background: "linear-gradient(0deg, #19a03f 0%, #2bc456 100%)" }
                    : { border: "2px solid #ea1d13", background: "linear-gradient(0deg, #ea1d13 0%, #ff3c32 100%)" };

                  return (
                    <div style={{ width: "100%", margin: "0px" }} key={idx}>
                      <Button style={{ width: "100%", ...feedbackStyles }}>
                        <div dangerouslySetInnerHTML={{ __html: answer.value }} />
                      </Button>
                    </div>
                  );
                })}
              </div>

              {!showColouredFeedback && (
                <SubmitButton onClick={handleSubmitAnswer}>
                  <div>Submit</div>
                </SubmitButton>
              )}
            </Container>
          )}
        </Scale>
      </Body>
    </NewGrammarGame>
  );
}

const Body = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 40px;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 100px;
`;

const Question = styled.div`
  background: #FFF;
  user-select: none;
  width: 1000px;
  height: 100%;
  line-height: 50px;
  font-size: 4em;
  margin: 5px 0px;
  text-align: center;
  border: none;
  border-radius: 10px;
  padding: 0.5em;
  margin-bottom: 2%;
`;

const Button = styled.div`
  text-align: center;
  width: 100%;
  height: 100%;
  line-height: 40px;
  font-size: 4em;
  margin: 0px 0px 1.5% 0px;
  color: #fff;
  user-select: none;
  background: linear-gradient(0deg, rgba(234, 146, 19, 1) 0%, rgba(255, 186, 32, 1) 100%);
  border: 2px solid #e88f12;
  padding: 0.5em;
  box-sizing: border-box;
  border-radius: 0.25em;
  cursor: pointer;
  @media (min-width: 1025px) and (max-width: 1440px) { font-size: 2.5em; }
`;

const SelectedButton = styled.div`
  text-align: center;
  width: 100%;
  height: 100%;
  line-height: 40px;
  font-size: 4em;
  margin: 0px 0px 1.5% 0px;
  color: #fff;
  user-select: none;
  background: linear-gradient(0deg, rgba(45, 168, 35, 1) 0%, rgba(36, 138, 29, 1) 100%);
  border: 2px solid #1d7d16;
  padding: 0.5em;
  box-sizing: border-box;
  border-radius: 0.25em;
  cursor: pointer;
  @media (min-width: 1025px) and (max-width: 1440px) { font-size: 2.5em; }
`;

const SubmitButton = styled.div`
  align-self: center;
  text-align: center;
  width: 50%;
  margin: 20px;
  line-height: 40px;
  font-size: 4em;
  color: #fff;
  user-select: none;
  background: linear-gradient(0deg, rgba(234, 146, 19, 1) 0%, rgba(255, 186, 32, 1) 100%);
  border: 2px solid #e88f12;
  padding: 0.5em;
  box-sizing: border-box;
  border-radius: 0.25em;
  cursor: pointer;
  box-shadow: 0 6px 0px #ba7109;
  &:active {
    transform: translateY(3px);
    box-shadow: 0 3px 0px #ba7109;
  }
  @media (min-width: 1025px) and (max-width: 1440px) { font-size: 2.5em; }
`;